import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { signalsAPI } from '../services/api'

interface Anomaly {
  type: string
  severity: number
  headline: string
  direction_symbol: string
  detail?: string
}

interface Signal {
  id: string
  symbol: string
  signal_type: string
  anomaly_score: number
  generated_at: string
  details: {
    spot: number
    atm_iv: number
    pcr: number
    anomalies: Anomaly[]
    strategy_hint?: string
  }
}

interface AccuracyData {
  [key: string]: {
    total_signals: number
    moved_1pct_30m: number
    moved_2pct_30m: number
    avg_move_30m: number
  }
}

type Tab = 'live' | 'history' | 'accuracy'

export default function Dashboard() {
  const [signals, setSignals] = useState<Signal[]>([])
  const [history, setHistory] = useState<Signal[]>([])
  const [accuracy, setAccuracy] = useState<AccuracyData>({})
  const [plan, setPlan] = useState('basic')
  const [nextScan, setNextScan] = useState('')
  const [loading, setLoading] = useState(true)
  const [lastUpdate, setLastUpdate] = useState('')
  const [activeTab, setActiveTab] = useState<Tab>('live')
  const navigate = useNavigate()

  const fetchSignals = async () => {
    try {
      const res = await signalsAPI.getLatest()
      setSignals(res.data.signals || [])
      setPlan(res.data.plan || 'basic')
      setNextScan(res.data.next_scan || '')
      setLastUpdate(new Date().toLocaleTimeString('en-IN'))
    } catch (err) {
      console.error('Failed to fetch signals', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchHistory = async () => {
    try {
      const res = await signalsAPI.getHistory(7)
      setHistory(res.data.signals || [])
    } catch (err) {
      console.error('Failed to fetch history', err)
    }
  }

  const fetchAccuracy = async () => {
    try {
      const res = await signalsAPI.getAccuracy()
      setAccuracy(res.data.accuracy || {})
    } catch (err) {
      console.error('Failed to fetch accuracy', err)
    }
  }

  useEffect(() => {
    fetchSignals()
    const interval = setInterval(fetchSignals, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (activeTab === 'history') fetchHistory()
    if (activeTab === 'accuracy') fetchAccuracy()
  }, [activeTab])

  const logout = () => {
    localStorage.clear()
    navigate('/login')
  }

  const severityColor = (score: number) => {
    if (score >= 70) return '#ef4444'
    if (score >= 40) return '#f59e0b'
    return '#00d4ff'
  }

  const scoreDisplay = (score: number) => `${score}/10`

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <span style={styles.logo}>AITradeMinds</span>
          <span style={styles.planBadge}>{plan.toUpperCase()}</span>
        </div>
        <div style={styles.headerRight}>
          {lastUpdate && <span style={styles.updateTime}>Updated {lastUpdate}</span>}
          <button style={styles.logoutBtn} onClick={logout}>Logout</button>
        </div>
      </div>

      {/* Stats bar */}
      <div style={styles.statsBar}>
        <div style={styles.stat}>
          <span style={styles.statValue}>{signals.length}</span>
          <span style={styles.statLabel}>Anomalies</span>
        </div>
        <div style={styles.stat}>
          <span style={styles.statValue}>203</span>
          <span style={styles.statLabel}>Stocks Scanned</span>
        </div>
        <div style={styles.stat}>
          <span style={styles.statValue}>{nextScan || 'Market Closed'}</span>
          <span style={styles.statLabel}>Next Scan</span>
        </div>
        <div style={styles.stat}>
          <span style={styles.statValue}>15m</span>
          <span style={styles.statLabel}>Interval</span>
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        {(['live', 'history', 'accuracy'] as Tab[]).map(tab => (
          <button
            key={tab}
            style={{
              ...styles.tab,
              ...(activeTab === tab ? styles.tabActive : {}),
            }}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'live' && '📡 Live Signals'}
            {tab === 'history' && '📋 History'}
            {tab === 'accuracy' && '🎯 Accuracy'}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={styles.content}>

        {/* LIVE TAB */}
        {activeTab === 'live' && (
          loading ? (
            <div style={styles.empty}>Loading signals...</div>
          ) : signals.length === 0 ? (
            <div style={styles.empty}>
              <div style={styles.emptyIcon}>📊</div>
              <div>No anomalies detected in this scan</div>
              <div style={styles.emptyHint}>Market may be low-conviction or pipeline warming up</div>
              <div style={styles.emptyHint}>Next scan at {nextScan}</div>
            </div>
          ) : (
            <div style={styles.grid}>
              {signals.map(signal => (
                <div key={signal.id} style={styles.card}>
                  <div style={styles.cardHeader}>
                    <span style={styles.symbol}>{signal.symbol}</span>
                    <span style={{
                      ...styles.scoreBadge,
                      background: severityColor(signal.anomaly_score),
                    }}>
                      {scoreDisplay(signal.anomaly_score)}
                    </span>
                  </div>
                  <div style={styles.cardMeta}>
                    {signal.details?.spot > 0 && (
                      <span style={styles.metaItem}>₹{signal.details?.spot?.toLocaleString('en-IN') || '-'}</span>
                    )}
                    {signal.details?.atm_iv > 0 && (
                      <span style={styles.metaItem}>IV: {signal.details.atm_iv}%</span>
                    )}
                    {signal.details?.pcr > 0 && (
                      <span style={styles.metaItem}>PCR: {signal.details.pcr}</span>
                    )}
                  </div>
                  {signal.details?.anomalies?.map((a, i) => (
                    <div key={i} style={styles.anomalyRow}>
                      <span style={styles.dirSymbol}>{a.direction_symbol}</span>
                      <div>
                        <div style={styles.anomalyType}>{a.type}</div>
                        <div style={styles.anomalyHeadline}>{a.headline}</div>
                        {a.detail && <div style={styles.anomalyDetail}>{a.detail}</div>}
                      </div>
                    </div>
                  ))}
                  {signal.details?.strategy_hint && (
                    <div style={styles.hint}>💡 {signal.details.strategy_hint}</div>
                  )}
                  <div style={styles.timestamp}>
                    {new Date(signal.generated_at).toLocaleTimeString('en-IN')}
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {/* HISTORY TAB */}
        {activeTab === 'history' && (
          plan === 'basic' ? (
            <div style={styles.locked}>
              <div style={styles.lockIcon}>🔒</div>
              <div style={styles.lockTitle}>Pro Feature</div>
              <div style={styles.lockText}>Signal history requires Pro or Elite plan</div>
              <button style={styles.upgradeBtn}>Upgrade to Pro — ₹2,499/month</button>
            </div>
          ) : history.length === 0 ? (
            <div style={styles.empty}>
              <div style={styles.emptyIcon}>📋</div>
              <div>No historical signals yet</div>
              <div style={styles.emptyHint}>History builds up during market hours</div>
            </div>
          ) : (
            <div style={styles.historyTable}>
              <div style={styles.tableHeader}>
                <span style={styles.th}>Time</span>
                <span style={styles.th}>Symbol</span>
                <span style={styles.th}>Signal</span>
                <span style={styles.th}>Score</span>
                <span style={styles.th}>Price</span>
                <span style={styles.th}>PCR</span>
              </div>
              {history.map(s => (
                <div key={s.id} style={styles.tableRow}>
                  <span style={styles.td}>
                    {new Date(s.generated_at).toLocaleTimeString('en-IN', {
                      hour: '2-digit', minute: '2-digit'
                    })}
                  </span>
                  <span style={{...styles.td, color: '#00d4ff', fontWeight: 700}}>
                    {s.symbol}
                  </span>
                  <span style={styles.td}>{s.signal_type}</span>
                  <span style={{
                    ...styles.td,
                    color: severityColor(s.anomaly_score)
                  }}>
                    {scoreDisplay(s.anomaly_score)}
                  </span>
                  <span style={styles.td}>
                    ₹{s.details?.spot?.toLocaleString('en-IN') || '-'}
                  </span>
                  <span style={styles.td}>{s.details?.pcr || '-'}</span>
                </div>
              ))}
            </div>
          )
        )}

        {/* ACCURACY TAB */}
        {activeTab === 'accuracy' && (
          plan === 'basic' ? (
            <div style={styles.locked}>
              <div style={styles.lockIcon}>🔒</div>
              <div style={styles.lockTitle}>Pro Feature</div>
              <div style={styles.lockText}>Signal accuracy analytics requires Pro or Elite plan</div>
              <button style={styles.upgradeBtn}>Upgrade to Pro — ₹2,499/month</button>
            </div>
          ) : Object.keys(accuracy).length === 0 ? (
            <div style={styles.empty}>
              <div style={styles.emptyIcon}>🎯</div>
              <div>Accuracy data building up</div>
              <div style={styles.emptyHint}>
                Needs at least one full market day of signals with outcome tracking
              </div>
            </div>
          ) : (
            <div style={styles.accuracyGrid}>
              {Object.entries(accuracy).map(([type, stats]) => (
                <div key={type} style={styles.accuracyCard}>
                  <div style={styles.accuracyType}>{type}</div>
                  <div style={styles.accuracyStats}>
                    <div style={styles.accuracyStat}>
                      <span style={styles.accuracyValue}>{stats.total_signals}</span>
                      <span style={styles.accuracyLabel}>Total Signals</span>
                    </div>
                    <div style={styles.accuracyStat}>
                      <span style={{
                        ...styles.accuracyValue,
                        color: stats.moved_2pct_30m >= 50 ? '#22c55e' : '#f59e0b'
                      }}>
                        {stats.moved_2pct_30m}%
                      </span>
                      <span style={styles.accuracyLabel}>Moved 2%+ in 30m</span>
                    </div>
                    <div style={styles.accuracyStat}>
                      <span style={styles.accuracyValue}>{stats.avg_move_30m}%</span>
                      <span style={styles.accuracyLabel}>Avg Move 30m</span>
                    </div>
                  </div>
                  {/* Progress bar */}
                  <div style={styles.progressBar}>
                    <div style={{
                      ...styles.progressFill,
                      width: `${stats.moved_2pct_30m}%`,
                      background: stats.moved_2pct_30m >= 50 ? '#22c55e' : '#f59e0b',
                    }} />
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      <div style={styles.disclaimer}>
        ⚠️ Research intelligence only. Not SEBI registered investment advice.
      </div>
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  container: { background: '#0a0e1a', minHeight: '100vh', color: '#fff' },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 24px', borderBottom: '1px solid #1f2937', background: '#0d1117',
  },
  logo: { color: '#00d4ff', fontSize: 20, fontWeight: 700 },
  planBadge: {
    marginLeft: 12, background: '#1f2937', color: '#00d4ff',
    padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
  },
  headerRight: { display: 'flex', alignItems: 'center', gap: 16 },
  updateTime: { color: '#4b5563', fontSize: 12 },
  logoutBtn: {
    background: 'transparent', color: '#6b7280', border: '1px solid #374151',
    padding: '6px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13,
  },
  statsBar: { display: 'flex', borderBottom: '1px solid #1f2937', background: '#0d1117' },
  stat: { flex: 1, padding: '16px 24px', textAlign: 'center', borderRight: '1px solid #1f2937' },
  statValue: { display: 'block', fontSize: 24, fontWeight: 700, color: '#00d4ff' },
  statLabel: { display: 'block', fontSize: 11, color: '#6b7280', marginTop: 2 },
  tabs: {
    display: 'flex', padding: '0 24px',
    borderBottom: '1px solid #1f2937', background: '#0d1117',
  },
  tab: {
    padding: '14px 24px', background: 'transparent', border: 'none',
    color: '#6b7280', cursor: 'pointer', fontSize: 14, borderBottom: '2px solid transparent',
  },
  tabActive: { color: '#00d4ff', borderBottom: '2px solid #00d4ff' },
  content: { padding: 24 },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
    gap: 16,
  },
  card: { background: '#111827', border: '1px solid #1f2937', borderRadius: 12, padding: 20 },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  symbol: { fontSize: 20, fontWeight: 700, color: '#fff' },
  scoreBadge: { padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: 700, color: '#000' },
  cardMeta: { display: 'flex', gap: 12, marginBottom: 16 },
  metaItem: { color: '#6b7280', fontSize: 13 },
  anomalyRow: { display: 'flex', gap: 12, alignItems: 'flex-start', padding: '10px 0', borderTop: '1px solid #1f2937' },
  dirSymbol: { fontSize: 20, minWidth: 24 },
  anomalyType: { color: '#00d4ff', fontSize: 11, fontWeight: 700, marginBottom: 2 },
  anomalyHeadline: { color: '#e5e7eb', fontSize: 13 },
  anomalyDetail: { color: '#6b7280', fontSize: 12, marginTop: 4 },
  hint: { background: '#1f2937', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#9ca3af', marginTop: 12 },
  timestamp: { color: '#374151', fontSize: 11, marginTop: 12 },
  empty: { textAlign: 'center', padding: '80px 20px', color: '#6b7280', fontSize: 16 },
  emptyIcon: { fontSize: 48, marginBottom: 16 },
  emptyHint: { fontSize: 13, color: '#4b5563', marginTop: 8 },
  locked: { textAlign: 'center', padding: '80px 20px' },
  lockIcon: { fontSize: 48, marginBottom: 16 },
  lockTitle: { fontSize: 20, fontWeight: 700, color: '#fff', marginBottom: 8 },
  lockText: { color: '#6b7280', marginBottom: 24 },
  upgradeBtn: {
    background: '#00d4ff', color: '#000', border: 'none',
    padding: '12px 32px', borderRadius: 8, fontSize: 15,
    fontWeight: 700, cursor: 'pointer',
  },
  historyTable: { background: '#111827', borderRadius: 12, overflow: 'hidden' },
  tableHeader: {
    display: 'grid', gridTemplateColumns: '120px 120px 1fr 80px 100px 80px',
    padding: '12px 20px', background: '#1f2937', gap: 8,
  },
  th: { color: '#6b7280', fontSize: 12, fontWeight: 700, textTransform: 'uppercase' },
  tableRow: {
    display: 'grid', gridTemplateColumns: '120px 120px 1fr 80px 100px 80px',
    padding: '12px 20px', borderTop: '1px solid #1f2937', gap: 8,
    alignItems: 'center',
  },
  td: { color: '#e5e7eb', fontSize: 13 },
  accuracyGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: 16,
  },
  accuracyCard: { background: '#111827', border: '1px solid #1f2937', borderRadius: 12, padding: 20 },
  accuracyType: { color: '#00d4ff', fontWeight: 700, fontSize: 14, marginBottom: 16 },
  accuracyStats: { display: 'flex', justifyContent: 'space-between', marginBottom: 16 },
  accuracyStat: { textAlign: 'center' },
  accuracyValue: { display: 'block', fontSize: 24, fontWeight: 700, color: '#fff' },
  accuracyLabel: { display: 'block', fontSize: 11, color: '#6b7280', marginTop: 4 },
  progressBar: { height: 6, background: '#1f2937', borderRadius: 3, overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: 3, transition: 'width 0.3s ease' },
  disclaimer: {
    textAlign: 'center', padding: '16px', color: '#374151',
    fontSize: 11, borderTop: '1px solid #1f2937',
  },
}